import type React from 'react'
import { useCallback } from 'react'
import type { ProfileColumnDef } from './types'

interface UseProfileTableHeaderStyleParams<TFixedKey extends string, TMiddleKey extends string> {
    activeColumn: TMiddleKey | null
    overColumn: TMiddleKey | null
    resizingColumn: TMiddleKey | null
    visibleMiddleColumns: Array<{ key: string }>
    accentColor: string
    /**
     * When true, apply the computed `getColumnWidth` to the header cell even
     * when not collapsed. Required for `table-layout: fixed` (virtualized
     * tables) where header widths govern column sizing.
     */
    forceWidth?: boolean
    getColumnWidth: (key: TFixedKey | TMiddleKey) => number
    isColumnCollapsed: (key: TFixedKey | TMiddleKey) => boolean
}

/**
 * Builds a CSSProperties object for a header cell, handling:
 *   - dynamic column width / collapsed state
 *   - drop indicators during drag-and-drop
 *   - resize highlight
 *   - drag dimming
 */
export const useProfileTableHeaderStyle = <TFixedKey extends string, TMiddleKey extends string>({
    activeColumn,
    overColumn,
    resizingColumn,
    visibleMiddleColumns,
    accentColor,
    forceWidth = false,
    getColumnWidth,
    isColumnCollapsed,
}: UseProfileTableHeaderStyleParams<TFixedKey, TMiddleKey>) =>
    useCallback(
        (column: ProfileColumnDef<string>): React.CSSProperties => {
            const width = getColumnWidth(column.key as TFixedKey | TMiddleKey)
            const collapsed = isColumnCollapsed(column.key as TFixedKey | TMiddleKey)
            const isDragged = activeColumn === column.key
            const isDropTarget = overColumn === column.key && activeColumn !== null && activeColumn !== column.key
            const activeIndex = activeColumn ? visibleMiddleColumns.findIndex((col) => col.key === activeColumn) : -1
            const overIndex = overColumn ? visibleMiddleColumns.findIndex((col) => col.key === overColumn) : -1
            const dropIndicatorSide: 'left' | 'right' | undefined =
                isDropTarget && activeIndex >= 0 && overIndex >= 0 ? (activeIndex < overIndex ? 'right' : 'left') : undefined

            const highlightShadow =
                resizingColumn === column.key
                    ? `inset -2px 0 0 ${accentColor}`
                    : dropIndicatorSide
                      ? `inset ${dropIndicatorSide === 'left' ? '3px' : '-3px'} 0 0 ${accentColor}`
                      : undefined

            return {
                minWidth: collapsed || forceWidth ? width : column.minWidth,
                width: collapsed || forceWidth ? width : undefined,
                padding: collapsed ? '0 4px' : '0 8px',
                position: 'relative',
                userSelect: 'none',
                verticalAlign: 'middle',
                textAlign: column.align ?? 'left',
                boxShadow: highlightShadow,
                opacity: isDragged ? 0.55 : 1,
                transition: 'box-shadow 150ms ease, background-color 180ms ease, opacity 150ms ease',
                zIndex: isDragged ? 30 : 1,
            }
        },
        [accentColor, activeColumn, forceWidth, getColumnWidth, isColumnCollapsed, overColumn, resizingColumn, visibleMiddleColumns]
    )
